import Classnames from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Image from 'components/ImageV2';
import ProductBadge, { PLACEMENTS } from 'components/ProductBadge';
import { Sub, SubBold } from 'components/Sub';
import { IMAGE_BG_COLOR, IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import { mediumUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';

const Colorway = ({
  url,
  masterName,
  colorName,
  price,
  compareAtPrice,
  image,
  handle,
  isDesktop,
  trackNavigation,
  enableDiscountPriceColor,
  badge,
  className,
}) => (
  <div
    className={Classnames('Colorway', className, {
      'Colorway--has-margin': isDesktop,
      'Colorway--no-margin': !isDesktop,
      'Colorway--discount-price-colored': compareAtPrice && enableDiscountPriceColor,
    })}
  >
    <a
      className="Colorway__link"
      href={url}
      onClick={event => trackNavigation({ handle, url, event })}
      aria-label="View product details"
    >
      <div className="Colorway__image-container">
        <Image
          src={get(image, ['src'])}
          alt={get(image, ['alt'])}
          className="Colorway__image-container Colorway__image-container--backdrop"
          width={IMAGE_WIDTHS.W_800}
          ratio={IMAGE_RATIOS.R_1x1}
          transformations={{
            backgroundColor: IMAGE_BG_COLOR,
          }}
        />

        {badge && <ProductBadge badgeText={badge} placement={PLACEMENTS.BOTTOM} />}
      </div>
      <div className="Colorway__clickable-details">
        <SubBold className="Colorway__master-name">{masterName}</SubBold>
        {colorName && <Sub className="Colorway__color-name">{colorName}</Sub>}
      </div>
    </a>
    <div className="Colorway__non-clickable-details">
      <Sub className="Colorway__price">
        {price}
        {compareAtPrice && <span className="Colorway__compare-at-price">{compareAtPrice}</span>}
      </Sub>
    </div>
    <style jsx>
      {`
        .Colorway__image-container {
          position: relative;
          width: 100%;
          background-color: ${IMAGE_BG_COLOR};
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }

          to {
            opacity: 1;
          }
        }

        .Colorway {
          position: relative;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
        }

        .Colorway__link {
          text-decoration: none;
          display: block;
        }

        .Colorway:focus,
        .Colorway__link:focus {
          outline: none;
        }

        .Colorway__link.focused {
          outline: 2px solid ${Theme.get('colors-black')};
        }

        .Colorway--v2.Colorway--no-margin .Colorway__clickable-details {
          margin-bottom: ${Theme.get('spacing')}px;
        }

        .Colorway--no-margin .Colorway__clickable-details {
          padding: ${Theme.get('spacing') * 2}px ${Theme.get('spacing') * 3}px 0;
        }

        .Colorway--no-margin .Colorway__non-clickable-details {
          padding: 0 ${Theme.get('spacing') * 3}px;
        }

        .Colorway--has-margin .Colorway__clickable-details {
          padding: ${Theme.get('spacing') * 2}px 0 0;
        }

        .Colorway__compare-at-price {
          margin-left: ${Theme.get('spacing')}px;
          text-decoration: line-through;
          color: ${Theme.get('colors-discount-grey')};
        }

        @media ${mediumUp} {
          .Colorway--no-margin .Colorway__clickable-details {
            min-height: auto;
          }
        }
      `}
    </style>
    <style jsx global>
      {`
        .Colorway__image-container {
          width: 100%;
        }

        .Colorway--v2 .Sub.Colorway__color-name {
          margin-bottom: 0;
        }

        .Sub.Colorway__color-name,
        .Sub.Colorway__master-name,
        .Sub.Colorway__price {
          margin: 0 0 ${Theme.get('spacing-px')};
        }

        .Colorway--discount-price-colored .Sub.Colorway__price {
          color: ${Theme.get('colors-discount-red')};
        }
      `}
    </style>
  </div>
);

export const COLORWAY_SHAPE = {
  url: PropTypes.string.isRequired,
  masterName: PropTypes.string.isRequired,
  colorName: PropTypes.string,
  price: PropTypes.string.isRequired,
  compareAtPrice: PropTypes.string,
  image: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  }).isRequired,
  handle: PropTypes.string,
  isDesktop: PropTypes.bool,
  enableDiscountPriceColor: PropTypes.bool,
  trackNavigation: PropTypes.func,
  badge: PropTypes.string,
  className: PropTypes.string,
};

Colorway.propTypes = COLORWAY_SHAPE;

Colorway.defaultProps = {
  isDesktop: true,
  enableDiscountPriceColor: false,
  compareAtPrice: null,
  handle: null,
  badge: '',
  className: '',
  colorName: null,
  trackNavigation: () => {},
};

export default Colorway;
